/* ---------------------------------------------------------------------------
   Which shell the application draws, and what that shell is allowed to reach.

   Two shells over one store. The desk shell is the eight screens this
   application has, with a nav pane and a title bar rail — though the eighth,
   People, is drawn only for somebody who may grant a role. The field shell is
   three tabs for an inspector at a structure, and it reaches five screens.

   **WHY THIS IS STATE AND NOT A MEDIA QUERY.** A media query can restyle a
   screen. It cannot decline to have one. "Hide what belongs at a desk" is a
   routing rule, not a styling rule: under CSS alone the network summary is still
   at `#/summary`, still one keystroke away, still built and rendered on a phone
   before being hidden. The shell has to be something src/main.ts and
   src/core/router.ts can both read, so it is a field in state.

   **THE DECISION IS A PURE FUNCTION, ON PURPOSE.** `chooseShell` takes a width
   and a flag rather than reading `window`, because a rule that reads the DOM
   inline is a rule no check can reach — jsdom has no `matchMedia` worth the
   name. `probeShell` is the two lines that gather the facts, and it is the only
   part of this file that cannot be tested. Same argument as `stampNewInspection`
   and `geometryNotice`: if a rule matters and its surroundings cannot run in CI,
   move the rule.

   **AN ESCAPE HATCH THAT NEEDS NO DEPLOY.** `?shell=field` and `?shell=desk`
   override everything. A district tablet that reports itself as a desktop, or a
   phone that reports as a tablet, is otherwise a support call with no answer;
   this one is a sentence somebody can say over the phone. Exactly the argument
   `?sw=off` is there for — see src/core/install.ts.
   --------------------------------------------------------------------------- */

import type { Screen, Shell } from '../data/types.ts';
import type { AppId } from './apps.ts';

/**
 * Below this many CSS pixels wide, a device gets the field shell.
 *
 * 900 rather than a phone width, because the field shell is for tablets too and
 * a 10-inch tablet in portrait is around 800. It is deliberately the same figure
 * as app.css's oldest breakpoint: above it the desk layout has room for its nav
 * pane and its details column, and below it that layout was already folding.
 */
export const FIELD_MAX_WIDTH = 900;

/**
 * Which shell a device should open in, absent a stored choice or an override.
 *
 * Both facts are required, and neither alone is enough. Width alone hands the
 * field shell to a desk browser somebody has narrowed, which is how this
 * feature would come to be blamed for a register nobody could sort. A coarse
 * pointer alone hands it to a large touchscreen — the kiosk case, and a
 * plausible way for a division office to end up with an inspector's interface.
 */
export function chooseShell(width: number, coarsePointer: boolean): Shell {
  if (!coarsePointer) return 'desk';
  return width <= FIELD_MAX_WIDTH ? 'field' : 'desk';
}

/**
 * A shell named in the address, or null when none is.
 *
 * Takes the query string rather than reading `location`, for the same reason
 * `chooseShell` takes its inputs. Anything other than the two known values is
 * null rather than an error: a mistyped override should fall through to the
 * probe, not strand somebody on a shell they cannot name.
 */
export function shellOverride(search: string): Shell | null {
  const value = new URLSearchParams(search).get('shell');
  return value === 'field' || value === 'desk' ? value : null;
}

/** The two facts, measured. The only untestable lines in this file. */
export function probeShell(): Shell {
  if (typeof window === 'undefined') return 'desk';
  const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  return chooseShell(window.innerWidth, coarse);
}

/**
 * The screens the field shell may reach. Everything else is desk work.
 *
 * `detail` is in it but is not a tab: it is pushed from the Bridges list or from
 * a map marker. `guide` is in it and is not a tab either, for the same reason —
 * it is opened from Options. The three tabs are `list`, `network` and `options`.
 *
 * What is absent matters more than what is present, so it is worth saying why
 * each one is: `dashboard` and `summary` are network-wide reports for somebody
 * with a printer and a whole register in view; `report` is the inventory sheet,
 * which is a document, and nobody is printing in a gorge; `people` is
 * administering the application rather than using it, which is the same line
 * FIELD.md draws around bulk import and rate tables.
 *
 * `guide` is the one addition that is NOT desk work, and the line it tests is
 * worth stating. Everything else excluded here is excluded because it is a
 * document or an administration task. Documentation is neither: an inspector on
 * a district phone is the least likely of this application's readers to have been
 * shown it by somebody who already knew it, so a guide the field shell refused
 * would be withheld from exactly the person it is for.
 */
export const FIELD_SCREENS: ReadonlySet<Screen> = new Set<Screen>([
  'list',
  'network',
  'detail',
  'options',
  'guide',
]);

/** The tabs, in the order they are drawn. `detail` is reachable but not a tab. */
export const FIELD_TABS: readonly Screen[] = ['list', 'network', 'options'];

/**
 * Whether this shell refuses to draw this screen.
 *
 * The load-bearing half of the feature, and the reason it is a named function
 * rather than a `Set.has` at each call site: a check that only proves the three
 * tabs are present would pass just as well against a field shell that also
 * rendered the other three.
 */
export function shellRefuses(shell: Shell, screen: Screen): boolean {
  return shell === 'field' && !FIELD_SCREENS.has(screen);
}

/**
 * Where a refused screen sends somebody instead.
 *
 * The register, not the record: a link to `#/summary` opened on a phone has said
 * nothing about which structure it meant, and the persisted selection is not an
 * answer to a question nobody asked.
 */
export const FIELD_FALLBACK: Screen = 'list';

/**
 * The screen a field shell should show, given the one it was asked for.
 *
 * Kept beside `shellRefuses` rather than folded into it because the two answer
 * different questions — "may this be drawn" and "then what" — and the router
 * needs the second while src/main.ts needs the first.
 */
export function shellScreen(shell: Shell, screen: Screen): Screen {
  return shellRefuses(shell, screen) ? FIELD_FALLBACK : screen;
}

/**
 * The screens the desk draws on its start page rather than in an application.
 *
 * THE REGISTER AND THE NETWORK ARE THE HOME PAGE on a desk: the list of
 * structures and where they are, with everything about one structure in the
 * applications. Bridge Inspection still owns both — the field shell's Bridges
 * and Map tabs are exactly these two, and a phone has no start page to put them
 * on — so this is a rule of the desk shell, not of the app, and it lives here
 * beside the field shell's own for the same reason that one does.
 */
export const DESK_HOME_SCREENS: ReadonlySet<Screen> = new Set<Screen>(['list', 'network']);

/** Whether the desk sends this screen to its start page instead. */
export function deskSendsHome(shell: Shell, screen: Screen): boolean {
  return shell === 'desk' && DESK_HOME_SCREENS.has(screen);
}

/** Which pieces of chrome a shell puts around a screen. */
export interface Chrome {
  titlebar: boolean;
  /** The start page's navigation down the left. */
  navpane: boolean;
  /** The app page's ribbon, in place of the nav pane. See src/components/ribbon.ts. */
  ribbon: boolean;
  tabbar: boolean;
  splitDetail: boolean;
}

/**
 * The chrome around a screen, for a shell and the app on it.
 *
 * THE DESK SHELL HAS TWO PAGES. The start page (`home`) keeps a nav pane — its
 * own three screens and the four applications — and the app page swaps that for
 * the ribbon, whose tabs are the applications and whose first group is the app's
 * screens. The field shell is neither: one app, a tab bar, no chrome above it.
 */
export function shellChrome(shell: Shell, app: AppId = 'home'): Chrome {
  const field = shell === 'field';
  return {
    titlebar: !field,
    navpane: !field && app === 'home',
    ribbon: !field && app !== 'home',
    tabbar: field,
    splitDetail: !field,
  };
}
